import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatOptionModule } from '@angular/material/core';
import { NgFor, NgIf } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@copo/mat-datepicker-new';

import { CodeValue } from '../../../../shared/models/boc-available-value';
import { CodeValue as RddType } from '../../../../shared/models/code-value';
import { UserService } from '../../../../shared/services/user.service';
import { EorderWizardService } from '../../../services/eorder-wizard.service';
import { BocClient } from '../../../../shared/models/boc-client-transac';
import { CopoDatepickerInputDirective } from '../../../../shared/directives/date-picker.directive';
import { SortOptionsPipe } from '../../../../shared/pipes/sorting.pipe';
import { BusinessType } from '../../../../shared/models/business-type';
import { FormEorder } from '../../../models/form-eorder';

const RDD_TYPE_DAY = 'D';
const RDD_TYPE_WEEK = 'W';
const SUCCESS_MESSAGE = 'Values have been applied on all items';

@Component({
	selector: 'copo-apply-all',
	templateUrl: './apply-all.component.html',
	styleUrl: './apply-all.component.css',
	imports: [
		MatCheckboxModule,
		FormsModule,
		MatFormFieldModule,
		TranslateModule,
		MatSelectModule,
		NgFor,
		MatOptionModule,
		MatTooltipModule,
		MatButtonModule,
		MatIconModule,
		NgIf,
		MatInputModule,
		CopoDatepickerInputDirective,
		MatDatepickerModule,
		SortOptionsPipe
	],
})

export class ApplyAllComponent implements OnInit {
	@Output() readonly applyAllEvent: EventEmitter<Map<string, string>> = new EventEmitter<Map<string, string>>();
	@Input() displayShipTo = false;
	@Input() defaultValues = new Map<string, string>();
	@Input() businessTypes?: BusinessType[] = [];
	readonly form: FormEorder;
	isTransportModeChecked = false;
	isShipToPartyChecked = false;
	isBusinessTypeChecked = false;
	isServiceLevelChecked = false;
	isRDDChecked = false;
	isXCarbChecked = false;
	transportModeSelected = '';
	shipToSelected = '';
	businessTypeSelected = '';
	serviceLevelSelected = '';
	xcarbSelected = '';
	rddTypeSelected = '';
	rddDateSelected = '';
	calStartDate!: Date;
	calEndDate!: Date;
	rddTypes: RddType[] = [
		{ code: RDD_TYPE_DAY, value: 'Day' },
		{ code: RDD_TYPE_WEEK, value: 'Week' }
	];

	configForBoc = { codeProp: 'businessId', valueProp: 'name' };
	tmodeList?: CodeValue[] = [];
	serviceLevelList?: CodeValue[] = [];
	xcarbParamList?: CodeValue[] = [];
	shipToList: BocClient[];
	selectedValues: { id: string; value: string }[] = [];
	constructor(
		private readonly eorderWizardService: EorderWizardService,
		private readonly userService: UserService,
	) {
		this.form = this.eorderWizardService.getStateEorderForm();
		this.tmodeList = this.userService.getBocAvailableValuesBasedOnCode('EORDR', 'TMODE');
		if (this.tmodeList) {
			this.tmodeList = this.tmodeList.filter(item => item.code !== 'DEFAU');
		}
		this.serviceLevelList = this.userService.getBocAvailableValuesBasedOnCode('EORDR', 'SERVICELEVEL');
		this.serviceLevelList = this.serviceLevelList?.sort((a, b) => a.code.localeCompare(b.code));
		this.xcarbParamList = this.userService.getBocAvailableValuesBasedOnCode('EORDR', 'XCARB');
		if (this.xcarbParamList && this.xcarbParamList.length > 0
			&& !this.xcarbParamList.some(param => param.code === '')) {
			this.xcarbParamList.unshift({
				code: '',
				description: 'None',
				isDefault: true
			});
		}
		this.shipToList = this.getShipToList();
		this.setCalendarStartEndDate();
	}

	ngOnInit(): void {
		for (const [key, value] of this.defaultValues.entries()) {
			switch (key) {
			case 'transportMode':
				this.transportModeSelected = value;
				break;
			case 'shipToParty':
				this.shipToSelected = value;
				break;
			case 'businessType':
				this.businessTypeSelected = value;
				break;
			case 'serviceLevel':
				this.serviceLevelSelected = value;
				break;
			case 'rddType':
				this.rddTypeSelected = value;
				break;
			case 'rdd':
				this.rddDateSelected = value;
				break;
			case 'xcarb':
				this.xcarbSelected = value;
				break;
			}
		}
	}

	getShipToList() {
		return this.eorderWizardService.shipToList || [];
	}

	compareWithFn(field1: string, field2: string) {
		return field1 === field2;
	}

	setCalendarStartEndDate() {
		const calEndDate = new Date();
		calEndDate.setFullYear(calEndDate.getFullYear() + 2);
		this.calStartDate = new Date();
		this.calEndDate = calEndDate;
	}

	onApplyAll() {
		const changedObjects = new Map<string, string>();
		if (this.isTransportModeChecked && this.transportModeSelected !== '') {
			changedObjects.set('transportMode', this.transportModeSelected);
		}
		if (this.isShipToPartyChecked && this.shipToSelected !== '') {
			changedObjects.set('shipToParty', this.shipToSelected);
		}
		if (this.isBusinessTypeChecked && this.businessTypeSelected !== '') {
			changedObjects.set('businessType', this.businessTypeSelected);
		}
		if (this.isServiceLevelChecked && this.serviceLevelSelected !== '') {
			changedObjects.set('serviceLevel', this.serviceLevelSelected);
		}
		if (this.isRDDChecked && this.rddTypeSelected !== '' && this.rddDateSelected !== '') {
			changedObjects.set('rddType', this.rddTypeSelected);
			changedObjects.set('rdd', this.rddDateSelected);
		}
		if (this.isXCarbChecked && this.xcarbSelected !== '') {
			changedObjects.set('xcarb', this.xcarbSelected);
		}
		if (changedObjects.size > 0) {
			this.eorderWizardService.showMessage(SUCCESS_MESSAGE);
			this.applyAllEvent.emit(changedObjects);
		}
	}

	getDateFormat() {
		return this.rddTypeSelected !== RDD_TYPE_WEEK ? 'date' : 'week';
	}
}
